import React, { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquare, ShieldCheck, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import { QrCode } from "@/components/QrCode";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const STATUS = {
  matched: ["Confirmed by parent", "bg-green-100 text-green-700"],
  verified: ["Verified by admin", "bg-green-100 text-green-700"],
  mismatch: ["Mismatch / Needs Review", "bg-red-100 text-red-700"],
  not_found: ["Receipt not found / Needs Review", "bg-red-100 text-red-700"],
  duplicate_receipt: ["Duplicate receipt no. / Needs Review", "bg-red-100 text-red-700"],
  late: ["Arrived after approval / Needs Review", "bg-amber-100 text-amber-700"],
};
const statusOf = (c) => (c.verified ? "verified" : c.match_status);
export const isCashCheque = (r) => r.mode === "cash" || r.mode === "cheque";

const ConfLine = ({ c, onVerify, busy }) => {
  const [label, cls] = STATUS[statusOf(c)] || ["Needs Review", "bg-red-100 text-red-700"];
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`pconf-${c.id}`}>
      <Badge className={cls}>{label}</Badge>
      <span>Mobile <b>{c.mobile}</b> · {formatDateTime(c.submitted_at_ist)} · {c.source === "sms" ? "Manual SMS" : "QR Form"} · Rcpt {c.receipt_number || "—"} · {inr(c.amount)}</span>
      {c.bonus_awarded && <span className="text-amber-600 font-semibold"><Star className="h-3 w-3 inline fill-amber-400" /> +5 awarded</span>}
      {onVerify && !c.verified && c.match_status !== "matched" && <Button size="sm" variant="outline" className="h-7 px-2" disabled={busy} onClick={() => onVerify(c)} data-testid={`pconf-verify-${c.id}`}>Mark verified</Button>}
    </div>
  );
};

export function ParentConfirmBox({ r, confs = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const sms = async () => {
    setBusy(true);
    try { await api.post(`/admin/collections/${r.id}/sms-confirmation`, { mobile }); toast.success("SMS confirmation recorded & verified"); setOpen(false); setMobile(""); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not record"); } finally { setBusy(false); }
  };
  const verify = async (c) => {
    if (!window.confirm(`Mark this confirmation (${inr(c.amount)}) as verified for this receipt?`)) return;
    setBusy(true);
    try { await api.post(`/admin/parent-confirmations/${c.id}/verify`, { collection_id: r.id }); toast.success("Verified"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not verify"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 space-y-1.5" data-testid={`pconf-box-${r.id}`}>
      <p className="text-xs font-semibold text-slate-700">Parent confirmation · Receipt {r.paper_receipt_number || "—"} · {r.mode.toUpperCase()}</p>
      {confs.length === 0 ? <p className="text-xs text-slate-500" data-testid={`pconf-none-${r.id}`}>Not confirmed yet — no bonus stars unless confirmed.</p>
        : confs.map((c) => <ConfLine key={c.id} c={c} onVerify={verify} busy={busy} />)}
      {open ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} maxLength={10} placeholder="Sender's 10-digit mobile" className="h-8 w-48" data-testid={`pconf-sms-mobile-${r.id}`} />
          <Button size="sm" className="h-8 bg-blue-900 hover:bg-blue-950" disabled={busy} onClick={sms} data-testid={`pconf-sms-save-${r.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & verify"}</Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      ) : <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => setOpen(true)} data-testid={`pconf-sms-open-${r.id}`}><MessageSquare className="h-3 w-3 inline mr-1" />Record SMS confirmation</button>}
    </div>
  );
}

export function ParentConfirmReview({ pendingRows, onChange }) {
  const [rows, setRows] = useState([]);
  const [link, setLink] = useState({});
  const [showQr, setShowQr] = useState(false);
  const load = useCallback(() => api.get("/admin/parent-confirmations/review").then(({ data }) => setRows(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const verify = async (c) => {
    const cid = link[c.id] || c.collection_id;
    if (!cid) { toast.error("Choose the receipt this belongs to"); return; }
    try { const { data } = await api.post(`/admin/parent-confirmations/${c.id}/verify`, { collection_id: cid }); toast.success(data.bonus_stars ? `Verified · +${data.bonus_stars} stars awarded` : "Verified"); load(); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not verify"); }
  };
  const dismiss = async (c) => { try { await api.post(`/admin/parent-confirmations/${c.id}/dismiss`); load(); } catch { toast.error("Could not dismiss"); } };
  const url = `${window.location.origin}/confirm-fees`;
  const options = pendingRows.filter(isCashCheque);
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2 mb-3" data-testid="pconf-review">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-bold text-amber-900"><ShieldCheck className="h-4 w-4 inline mr-1" />Parent confirmations needing review ({rows.length})</p>
        <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => setShowQr((s) => !s)} data-testid="pconf-qr-toggle">{showQr ? "Hide" : "Show"} QR for parents</button>
      </div>
      {showQr && <div className="rounded-lg bg-white p-3" data-testid="pconf-qr"><p className="text-xs mb-2">Permanent form link: <b data-testid="pconf-qr-url">{url}</b></p><QrCode value={url} label="PTG Confirm Fees QR" title="Parents Fees Confirmation" caption="Confirm Your Fees – Earn 5 Bonus Stars" /></div>}
      {rows.length === 0 ? <p className="text-xs text-amber-800">Nothing to review.</p> : rows.map((c) => (
        <div key={c.id} className="rounded-lg bg-white border border-amber-100 p-2 space-y-1.5" data-testid={`pconf-review-${c.id}`}>
          <ConfLine c={c} />
          <div className="flex flex-wrap items-center gap-2">
            {!c.collection_id && options.length > 0 && (
              <Select value={link[c.id] || ""} onValueChange={(v) => setLink((x) => ({ ...x, [c.id]: v }))}>
                <SelectTrigger className="h-8 w-72" data-testid={`pconf-link-${c.id}`}><SelectValue placeholder="Link to pending receipt…" /></SelectTrigger>
                <SelectContent>{options.map((r) => <SelectItem key={r.id} value={r.id}>Rcpt {r.paper_receipt_number || "—"} · {r.student_name} · {inr(r.amount)}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button size="sm" variant="outline" className="h-8" onClick={() => verify(c)} data-testid={`pconf-review-verify-${c.id}`}>{c.match_status === "late" ? "Verify & award stars" : "Mark verified"}</Button>
            <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={() => dismiss(c)} data-testid={`pconf-review-dismiss-${c.id}`}>Dismiss</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
